import { RefObject, useCallback, useLayoutEffect, useRef } from "react";
import { ProseMirror } from "@progress/kendo-react-editor";
import { getEditorView } from "../wysiwyg-editor/utils/domHelpers";

interface UseInsertAtCursorOptions {
  replyText: string;
  setReplyText: (text: string) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  editorRef?: RefObject<any>;
  isEmailKendoEditor?: boolean;
}

export function useInsertAtCursor({
  replyText,
  setReplyText,
  editorRef,
  isEmailKendoEditor = false
}: UseInsertAtCursorOptions) {
  // Tracks a pending cursor position to apply after React commits the DOM update.
  // useLayoutEffect fires synchronously after commit, guaranteeing the textarea value
  // is already updated before we set selectionStart/selectionEnd.
  const pendingCursorRef = useRef<{
    el: HTMLTextAreaElement;
    pos: number;
  } | null>(null);

  useLayoutEffect(() => {
    if (pendingCursorRef.current) {
      const { el, pos } = pendingCursorRef.current;
      pendingCursorRef.current = null;
      el.focus();
      el.selectionStart = pos;
      el.selectionEnd = pos;
    }
  });

  const insertContent = useCallback(
    (content: string) => {
      if (isEmailKendoEditor && editorRef) {
        try {
          const view = getEditorView(editorRef);
          const state = view?.state;
          const dispatch = view?.dispatch;
          const schema = state?.schema;
          const replaceSelection = state?.tr?.replaceSelection;
          if (schema && replaceSelection && dispatch) {
            const parser = ProseMirror.DOMParser.fromSchema(schema);
            const element = document.createElement("div");
            element.innerHTML = content;
            const slice = parser.parseSlice(element);
            const transaction = replaceSelection.call(state?.tr, slice);
            dispatch(transaction);
            return;
          }
        } catch {
          // fall through to state fallback below
        }
        setReplyText(content + replyText);
      } else {
        if (editorRef?.current) {
          const el = editorRef.current as HTMLTextAreaElement;
          // Browsers retain selectionStart/End after blur — always trust them.
          const start = el.selectionStart ?? 0;
          const end = el.selectionEnd ?? 0;
          // Read text from the live DOM value to avoid stale closure issues during rapid typing
          const currentText = el.value;
          const newText =
            currentText.slice(0, start) + content + currentText.slice(end);
          setReplyText(newText);
          // Only queue cursor restoration if the textarea is the active element.
          // When inserting from a modal (e.g. media library), el.focus() can trigger
          // Interstate's TextArea onChange with the stale DOM value, overwriting the insert.
          if (document.activeElement === el) {
            pendingCursorRef.current = { el, pos: start + content.length };
          }
        } else {
          // Fallback: prepend
          setReplyText(content + replyText);
        }
      }
    },
    [replyText, setReplyText, editorRef, isEmailKendoEditor]
  );

  return { insertContent };
}
